import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { VerksamhetshandelseLoggComponent } from "@components/loggning/verksamhetshandelseLogg.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { Dialogtyp, VisadEntitet } from "~app/common/components/loggning/constants/visningslogg";
import { IVerksamhetshandelseRequest } from "~app/common/components/loggning/contracts/verksamhetshandelse";
import { CanBeDirty } from "~app/common/guards/dirty.guard";
import { ArbetsorderRestriktionStatus } from "~app/common/models/constants";
import { EventService } from "~app/common/services/cqs/event.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IPlaneraBasdataResponse } from "~app/features/planera/model/IPlaneraBasdataResponse";
import { IArbetsorderVirkesorderRule } from "~app/features/virkesorder/virkesorder.models";
import { ArbetsorderViol3ButtonComponent } from "../../shared/components/arbetsorder-viol3-button/arbetsorder-viol3-button.component";
import { ArbetsorderAlertComponent } from "../arbetsorder-alert/arbetsorder-alert.component";
import { ArbetsorderAtgardComponent } from "../arbetsorder-atgard/arbetsorder-atgard.component";
import { ArbetsorderDestineringsalternativComponent } from "../arbetsorder-destineringsalternativ/arbetsorder-destineringsalternativ.component";
import { ArbetsorderDrivningspunkterComponent } from "../arbetsorder-drivningspunkter/arbetsorder-drivningspunkter.component";
import { ArbetsorderHandelssortimentsvalComponent } from "../arbetsorder-handelssortimentsval/arbetsorder-handelssortimentsval.component";
import { ArbetsorderHeaderComponent } from "../arbetsorder-header/arbetsorder-header.component";
import { ArbetsorderInformationComponent } from "../arbetsorder-info/arbetsorder-information.component";
import { ArbetsorderMaskinlagComponent } from "../arbetsorder-maskinlag/arbetsorder-maskinlag.component";
import { ArbetsorderPeriodsattPeriodComponent } from "../arbetsorder-periodsatt-period/arbetsorder-periodsatt-period.component";
import { ArbetsorderProduktionsdatumComponent } from "../arbetsorder-produktionsdatum/arbetsorder-produktionsdatum.component";
import { ArbetsorderProduktvalComponent } from "../arbetsorder-produktval/arbetsorder-produktval.component";
import { ArbetsorderRestriktionerComponent } from "../arbetsorder-restriktioner/arbetsorder-restriktioner.component";
import { ArbetsorderViolversionSelectorComponent } from "../arbetsorder-viol3/arbetsorder-violversion-selector/arbetsorder-violversion-selector.component";
import { DestineraArbetsorderHandelssortimentCommand } from "../commands/DestineraArbetsorderHandelssortimentCommand";
import { VerksamhetshandelseUpdatedEventMessage } from "../events/VerksamhetshandelseUpdatedEventMessage";
import { MapEvents } from "../events/map-events";
import { PlaneraKartaEventService } from "../planera-karta/planera-karta-event.service";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { PlaneraService } from "../services/planera.service";
import { IAtgardBarighetChanged } from "./planera-detail-models";

@Component({
    selector: "rask-planera-detail",
    templateUrl: "./planera-detail.component.html",
    styleUrls: ["./planera-detail.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ArbetsorderHeaderComponent,
        ArbetsorderAlertComponent,
        ArbetsorderPeriodsattPeriodComponent,
        ArbetsorderProduktionsdatumComponent,
        TogglePanelComponent,
        ArbetsorderInformationComponent,
        ArbetsorderMaskinlagComponent,
        ArbetsorderHandelssortimentsvalComponent,
        ArbetsorderProduktvalComponent,
        ArbetsorderDestineringsalternativComponent,
        ArbetsorderDrivningspunkterComponent,
        VerksamhetshandelseLoggComponent,
        ArbetsorderAtgardComponent,
        ArbetsorderViol3ButtonComponent,
        ArbetsorderViolversionSelectorComponent,
        ArbetsorderRestriktionerComponent
    ]
})
export class PlaneraDetailComponent implements OnInit, OnDestroy, CanBeDirty {
    readonly dirtyFormService = inject(DirtyFormService);
    arbetsorder: IArbetsorderDetail;
    @Input() arbetsorderid: number;
    @Input() basdata: IPlaneraBasdataResponse;
    defaultExpandInformation = false;
    destineringsalternativAsyncProgress: boolean; // [show-loader] på collapsable
    handelserReadDone: boolean;
    isDirty = false;
    ngUnsubscribe$ = new Subject<void>();
    overiddenValidationRules: IArbetsorderVirkesorderRule[];
    sumOlastaHandelser: string; // "badge" för antal nya händelser
    @Input() updatedAtgardBarighet: IAtgardBarighetChanged[];
    validationContext: any;
    verksamhetsHandelseRequest: IVerksamhetshandelseRequest; // dto för hämtning av händelser
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly busyService = inject(BusyService);
    private readonly eventService = inject(EventService);
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);
    private readonly planeraService = inject(PlaneraService);

    drivningspunkterStatusChanged($event): void {
        this.arbetsorderMediatorService.setDirty(true);
    }

    handleInformationPanelDisplay() {
        if (this.arbetsorder) {
            const requestedAtgardId = this.activatedRoute.snapshot.queryParamMap.get("atgardId");
            if (this.arbetsorder.atgarder && this.arbetsorder.atgarder.length > 0) {
                // params ärvs som standard i Angular vilket betyder att en tidigare atgardId kan vara satt som inte tillhör denna arbetsorder
                const atgardExists = (this.arbetsorder.atgarder.findIndex(atg => atg.id === parseInt(requestedAtgardId, 10)) > -1);
                // Gör så att Informationslimpen visas expanderad eller inte
                this.defaultExpandInformation = (!!requestedAtgardId && atgardExists);
                if (this.defaultExpandInformation) {
                    // Vi behöver vänta in nästa frame för att Angular vill ha en frame på sig för att instansiera/populera elementet först
                    window.requestAnimationFrame(() => {
                        document.getElementById("planera-limpa-information").scrollIntoView();
                    });
                }
            }
        }
    }

    ignoreChanges() {
        // Det tar tid för nya arbetsordern att laddas för att återställa värden
        this.isDirty = false;

        this.arbetsorderMediatorService.reload();
    }

    initHandelseloggWithEntitetsTyper(arbetsorder: IArbetsorderDetail): void {
        const handelseloggRequest: IVerksamhetshandelseRequest = {
            dialogTyp: Dialogtyp.Handelselogg,
            entiteter: [
                {
                    key: VisadEntitet.Arbetsorder,
                    value: arbetsorder.guid
                }
            ]
        };

        if (arbetsorder.atgarder.some) {
            arbetsorder.atgarder.forEach(atgard => {
                handelseloggRequest.entiteter.push({
                    key: VisadEntitet.Atgard,
                    value: atgard.guid
                });
            });
        }

        this.verksamhetsHandelseRequest = handelseloggRequest;
    }

    isDirtyFn = (): Promise<boolean> => new Promise(resolve => {
        if (this.isDirty) {
            const saveFn = (() => {
                this.save();
                resolve(true);
            });
            return this.dirtyFormService.dirtyFormModal(() => {
                this.ignoreChanges();
                resolve(true);
            }, saveFn, (arg) => {
                resolve(false);
            });
        } else {
            resolve(true);
            return undefined;
        }
    });

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.arbetsorderMediatorService.load(this.arbetsorderid);
        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                this.arbetsorder = changeEvent?.arbetsorderWrapper?.arbetsorder;
                if (this.arbetsorder != null) {
                    this.planeraKartaEventService.emit(MapEvents.selectArbetsorder, changeEvent?.arbetsorderWrapper);
                    this.initHandelseloggWithEntitetsTyper(this.arbetsorder);
                    this.handleInformationPanelDisplay();
                }
            });

        this.arbetsorderMediatorService.dirty
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(dirty => {
                this.isDirty = dirty;
            });

        this.eventService.subscribeEvent<VerksamhetshandelseUpdatedEventMessage>(VerksamhetshandelseUpdatedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => {
                if (ev.arbetsorderId === this.arbetsorder?.id) {
                    this.initHandelseloggWithEntitetsTyper(this.arbetsorder);
                }
            });
    }

    onAddDestineraCommand(command: DestineraArbetsorderHandelssortimentCommand): void {
        this.arbetsorder.commandManager.updateCommand(command);
    }

    onDestineraValidate(result: boolean): void {
        this.arbetsorder.isDestineringsalternativValidForSave = result;
    }

    onDestineringsalternativLoading(isLoading: boolean): void {
        this.destineringsalternativAsyncProgress = isLoading;
    }

    onHamtaDrivningspunkter = () => {
        const hamtaDrivningspunkterFunc = (ao: IArbetsorderDetail) => {
            this.busyService.addWork(this.planeraService.hamtaDrivningspunkterFranKontrakt(ao.id, ao.rowVersion)).subscribe(() => {
                this.arbetsorderMediatorService.reload(); // ny rowversion behövs efter nya drivningspunkter
            });
        };

        if (this.isDirty) {
            this.arbetsorderMediatorService.save(this.updatedAtgardBarighet, true, (ao) => {
                hamtaDrivningspunkterFunc(ao);
            }).subscribe();
        } else {
            hamtaDrivningspunkterFunc(this.arbetsorder);
        }
    };

    onProduktionsdatumUpdated = () => {
        this.arbetsorderMediatorService.setDirty();
    };

    onToggleHandelselogg($event) {
        this.handelserReadDone = $event;
    }

    setSumOlastaHandelser($event: string) {
        this.sumOlastaHandelser = $event;
    }

    get antalRestriktioner(): number {
        return this.arbetsorder?.restriktioner?.filter(x => x.status === ArbetsorderRestriktionStatus.Ejhanterad || x.status === ArbetsorderRestriktionStatus.Pagar).length ?? 0;
    }

    get restriktionerStatusIcon() {
        const restriktioner = this.arbetsorder?.restriktioner;
        if (restriktioner && restriktioner.length > 0 &&
            restriktioner.every(r => r.status === ArbetsorderRestriktionStatus.Hanterad)) {
            return this.restriktionerStatusIconStyle(ArbetsorderRestriktionStatus.Hanterad);
        }
        return this.restriktionerStatusIconStyle(ArbetsorderRestriktionStatus.Ejhanterad);
    }

    protected restriktionerStatusIconStyle(status: string): string {
        if (!this.arbetsorder?.restriktioner || this.arbetsorder.restriktioner.length === 0) {
            return "fa-solid fa-circle-minus";
        }
        if (status === ArbetsorderRestriktionStatus.Ejhanterad || status === ArbetsorderRestriktionStatus.Pagar) {
            return "fa-solid fa-circle-minus text-danger";
        }
        return "fa-solid fa-circle-minus";
    }

    private save() {
        this.arbetsorderMediatorService.save(this.updatedAtgardBarighet).subscribe();
    }
}
